import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Download, ImagePlus, Loader2, UploadCloud, XCircle } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { adminBulkUploadImages, adminProductIndex } from "../../services/api";

const SLOT_RE = /^(.+?)[-_]([1-4])$/;
const BATCH = 10;

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const parseFile = (file) => {
  let stem = file.name.replace(/\.[^.]+$/, "").trim();
  let slot = null;
  const m = stem.match(SLOT_RE);
  if (m) {
    stem = m[1];
    slot = Number(m[2]);
  }
  return { file, key: stem, slot };
};

const STATUS_STYLE = {
  matched: "bg-brand-gold/10 text-brand-gold",
  uploaded: "bg-emerald-100 text-emerald-700",
  no_match: "bg-amber-100 text-amber-700",
  skipped: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  pending: "bg-brand-sand/60 text-brand-stone",
};

export default function AdminBulkImages() {
  const [index, setIndex] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    adminProductIndex().then((res) => setIndex(res.items || [])).catch(() => toast.error("Could not load the bracelet index."));
  }, []);

  const bySku = useMemo(() => Object.fromEntries(index.filter((p) => p.sku).map((p) => [p.sku.toUpperCase(), p])), [index]);
  const bySlug = useMemo(() => Object.fromEntries(index.map((p) => [p.slug, p])), [index]);

  const matchFor = (key) => bySku[key.toUpperCase()] || bySlug[slugify(key)] || null;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList).filter((f) => /\.(png|jpe?g|webp)$/i.test(f.name));
    if (!incoming.length) {
      toast.error("Only PNG, JPG or WebP photos are accepted.");
      return;
    }
    setRows((prev) => [
      ...prev,
      ...incoming.map((file) => {
        const parsed = parseFile(file);
        return { ...parsed, product: matchFor(parsed.key), status: "pending", detail: "" };
      }),
    ]);
    setSummary(null);
  };

  const matchedRows = rows.filter((r) => r.product);

  const upload = async () => {
    const queue = rows.filter((r) => r.product && r.status !== "uploaded");
    if (!queue.length) return;
    setUploading(true);
    setProgress({ done: 0, total: queue.length });
    let acc = { uploaded: 0, skipped: 0, failed: 0 };
    for (let i = 0; i < queue.length; i += BATCH) {
      const batch = queue.slice(i, i + BATCH);
      try {
        const res = await adminBulkUploadImages(batch.map((r) => r.file));
        acc = { uploaded: acc.uploaded + res.uploaded, skipped: acc.skipped + res.skipped, failed: acc.failed + res.failed };
        const byName = Object.fromEntries(res.results.map((r) => [r.file, r]));
        setRows((prev) =>
          prev.map((row) => {
            const r = byName[row.file.name];
            if (!r || row.status === "uploaded") return row;
            return { ...row, status: r.status === "uploaded" ? "uploaded" : r.status, detail: r.detail || (r.product ? `${r.product} · photo ${r.slot}` : "") };
          })
        );
      } catch (err) {
        acc.failed += batch.length;
        setRows((prev) => prev.map((row) => (batch.some((b) => b.file === row.file) ? { ...row, status: "failed", detail: err?.response?.data?.detail || "Upload failed" } : row)));
      }
      setProgress({ done: Math.min(i + BATCH, queue.length), total: queue.length });
    }
    setSummary(acc);
    setUploading(false);
    toast.success(`Bulk upload finished — ${acc.uploaded} photo(s) attached.`);
    adminProductIndex().then((res) => setIndex(res.items || [])).catch(() => {});
  };

  const downloadSkuList = () => {
    const csv = ["SKU,Slug,Name,Photos", ...index.map((p) => `${p.sku},${p.slug},"${p.name.replace(/"/g, '""')}",${p.photos}`)].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "heshaura-sku-list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout
      title="Bulk Photo Upload"
      subtitle="Name photos with the bracelet SKU — they attach automatically"
      actions={
        <button type="button" onClick={downloadSkuList} data-testid="bulk-download-sku-list" className="flex items-center gap-2 border border-brand-charcoal/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal transition-colors hover:border-brand-orange hover:text-brand-orange">
          <Download className="h-4 w-4" /> SKU list (CSV)
        </button>
      }
    >
      <div className="space-y-6" data-testid="bulk-images-page">
        <div className="border border-brand-sand bg-brand-surface p-5 text-sm leading-relaxed text-brand-stone">
          <p className="font-semibold text-brand-charcoal">How matching works</p>
          <p className="mt-1">
            Name each photo with the bracelet's SKU: <span className="font-mono text-brand-charcoal">HSH-EVY-0001.jpg</span>{" "}becomes the primary photo,{" "}
            <span className="font-mono text-brand-charcoal">HSH-EVY-0001-2.jpg</span>{" "}the second, up to <span className="font-mono text-brand-charcoal">-4</span>.{" "}
            Product slugs also work. Photos are resized to web-friendly size automatically. Upload in batches of up to 50.
          </p>
        </div>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="bulk-dropzone"
          className="flex w-full flex-col items-center justify-center gap-3 border-2 border-dashed border-brand-gold/50 bg-brand-surface px-6 py-14 text-center transition-colors hover:border-brand-gold hover:bg-brand-champagne/40"
        >
          <UploadCloud className="h-8 w-8 text-brand-gold" strokeWidth={1.5} />
          <p className="font-serif text-xl text-brand-charcoal">Choose bracelet photos</p>
          <p className="text-xs uppercase tracking-[0.18em] text-brand-stone">PNG · JPG · WebP — multiple files welcome</p>
        </button>
        <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.webp" multiple className="hidden" data-testid="bulk-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

        {rows.length > 0 && (
          <div className="border border-brand-sand bg-brand-surface" data-testid="bulk-preview">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-sand px-5 py-4">
              <p className="text-sm text-brand-stone">
                <span className="font-semibold text-brand-charcoal">{matchedRows.length}</span> of {rows.length} photo(s) matched to bracelets
              </p>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => { setRows([]); setSummary(null); }} data-testid="bulk-clear" className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-stone hover:text-brand-orange">
                  Clear
                </button>
                <button
                  type="button"
                  onClick={upload}
                  disabled={uploading || !matchedRows.length}
                  data-testid="bulk-upload-btn"
                  className="flex items-center gap-2 bg-brand-charcoal px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  {uploading ? `Uploading ${progress.done}/${progress.total}…` : `Attach ${matchedRows.length} photo(s)`}
                </button>
              </div>
            </div>
            {uploading && (
              <div className="h-1 bg-brand-sand" data-testid="bulk-progress">
                <div className="h-full bg-brand-gold transition-all duration-300" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            )}
            <div className="max-h-[420px] divide-y divide-brand-sand overflow-y-auto">
              {rows.map((r, i) => (
                <div key={`${r.file.name}-${i}`} className="flex items-center gap-4 px-5 py-3" data-testid={`bulk-row-${i}`}>
                  <span className={`inline-flex min-w-[92px] items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${STATUS_STYLE[r.status === "pending" ? (r.product ? "matched" : "no_match") : r.status]}`}>
                    {r.status === "uploaded" && <CheckCircle2 className="h-3 w-3" />}
                    {(r.status === "failed" || (!r.product && r.status === "pending")) && <XCircle className="h-3 w-3" />}
                    {r.status === "pending" ? (r.product ? "Matched" : "No match") : r.status.replace("_", " ")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-xs text-brand-charcoal">{r.file.name}</p>
                    <p className="truncate text-xs text-brand-stone">
                      {r.product ? `${r.product.name} · ${r.product.sku}${r.slot ? ` · photo ${r.slot}` : ""}${r.product.photos ? ` · has ${r.product.photos} photo(s)` : ""}` : "Rename this file to a SKU from the CSV list"}
                    </p>
                  </div>
                  {r.detail && <p className="hidden max-w-[220px] truncate text-xs text-brand-stone md:block">{r.detail}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-3 gap-4" data-testid="bulk-summary">
            {[
              ["Attached", summary.uploaded, "text-emerald-700"],
              ["Skipped", summary.skipped, "text-amber-700"],
              ["Failed", summary.failed, "text-red-700"],
            ].map(([label, value, cls]) => (
              <div key={label} className="border border-brand-sand bg-brand-surface px-5 py-4 text-center">
                <p className={`font-serif text-3xl ${cls}`}>{value}</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-stone">{label}</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-brand-stone">
          Prefer one-by-one? Open a bracelet from <Link to="/admin/products" className="text-brand-orange underline-offset-2 hover:underline">Products</Link> and use its photo manager.
        </p>
      </div>
    </AdminLayout>
  );
}
